import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, SwitchCheckbox} from 'views/components';
import {WeeklyCalendarUsers} from 'views/module';
import {Translate, translate, isInactiveEvent, getInactiveEventAction} from 'views/module';

import UserRow from './UserRow';
import EventTile from './EventTile';
import SCHEDULE_SOURCES from '../schedule_source';

import moment from 'moment-timezone';

const ClientScheduleDashboard = (props) => {
  const progressWrap = props.statusProgressWrap;
  const scheduleSource = props.scheduleSource || SCHEDULE_SOURCES.LEGACY;

  const [clients, setClients] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState(undefined);
  const [loadingClients, setLoadingClients] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(true);

  const userFilter = [
    {
      key: 'SCHEDULED_FOR_THIS_WEEK',
      label: translate('client.ClientCalendar.Calendar.Filter.User.ScheduleForThisWeek'),
      filter: (user, scheduleEvents, weekDays) => {
        const hasOneEvent = weekDays.some((day) => {
          const dateStr = day.format('YYYY-MM-DD');
          return scheduleEvents?.[dateStr]?.length > 0;
        });
        return hasOneEvent;
      },
      defaultEnabled: true,
    },
  ];
  // Shift-progress filters describe what should still happen on a shift, so inactive events never satisfy them.
  const activeOnly = (filter) => (user, event) => !isInactiveEvent(event) && filter(user, event);

  const eventFilter = [
    {
      key: 'HAS_NOT_CONFIRMED_SHIFT',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.NotConfirmedShift'),
      filter: activeOnly((user, event) => {
        const confirmedShift = event.caregiverUpdates.some((e) => e.type === 'shiftConfirmation');
        return !confirmedShift;
      }),
    },
    {
      key: 'HAS_NOT_CLOCKED_IN',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.NotClockedIn'),
      filter: activeOnly((user, event) => {
        const clockedIn = event.caregiverUpdates.some((e) => e.type === 'clockInConfirmation');
        return !clockedIn;
      }),
    },
    {
      key: 'HAS_NOT_CLOCKED_OUT',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.NotClockedOut'),
      filter: activeOnly((user, event) => {
        const clockedOut = event.caregiverUpdates.some((e) => e.type === 'clockOutConfirmation');
        return !clockedOut;
      }),
    },
    {
      key: 'HAS_CLOCKED_IN_LATE',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.LateClockin'),
      filter: activeOnly((user, event) => {
        const clockedIn = event.caregiverUpdates.find((e) => e.type === 'clockInConfirmation');
        if (!clockedIn) {
          return false;
        }

        const isLate = moment(clockedIn.data.actualClockinTimeFormatted).isAfter(event.eventData.startTime);
        return isLate;
      }),
    },
    {
      key: 'HAS_CLOCKED_OUT_LATE',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.EarlyClockout'),
      filter: activeOnly((user, event) => {
        const clockedOut = event.caregiverUpdates.find((e) => e.type === 'clockInConfirmation');
        if (!clockedOut) {
          return false;
        }

        const isLate = moment(event.eventData.endTime).isAfter(clockedOut.data.actualClockoutTimeFormatted);
        return isLate;
      }),
    },
    {
      key: 'IS_CANCELLED',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.Cancelled'),
      filter: (user, event) => getInactiveEventAction(event) === 'CANCELLED',
    },
    {
      key: 'IS_DELETED',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.Deleted'),
      filter: (user, event) => getInactiveEventAction(event) === 'DELETED',
    },
    {
      key: 'IS_RESCHEDULED',
      label: translate('client.ClientCalendar.Calendar.Filter.Event.Rescheduled'),
      filter: (user, event) => getInactiveEventAction(event) === 'RESCHEDULED',
    },
  ];

  React.useEffect(() => {
    if (lastEvaluatedKey === null) {
      return;
    }
    progressWrap(async () => {
      setLoadingClients(true);
      const filter = [
        {
          key: 'status',
          value: 'ACTIVE',
        },
      ];

      const sortBy = {
        sortKey: 'createdAt',
        sortDirection: 'DESC',
      };

      const result = await scheduleSource.loadClients(
          filter,
          sortBy,
          lastEvaluatedKey,
      );
      setClients((e) => {
        return [
          ...e,
          ...result.list,
        ];
      });

      setLastEvaluatedKey(result.lastEvaluatedKey);
      setLoadingClients(false);
    }, {showProgress: false});
  }, [lastEvaluatedKey]);

  const loadUserSchedules = React.useCallback(async (user, startDay, endDay) => {
    return new Promise((resolve, reject) => {
      progressWrap(async () => {
        const start = moment(startDay).startOf('day').toISOString();
        const end = moment(endDay).endOf('day').toISOString();

        const {events} = await scheduleSource.loadClientRange(user.clientRn, start, end, {includeDeactivated: showInactive});

        const scheduleByDay = events.reduce((acc, event) => {
          // Loom keeps no per-event timezone, so its copy groups days in the reader's zone.
          const startTime = event.eventData.timezone ?
            moment.tz(event.eventData.startTime, event.eventData.timezone) :
            moment(event.eventData.startTime);
          const dateKey = startTime.format('YYYY-MM-DD');
          return {
            ...acc,
            [dateKey]: [
              ...(acc[dateKey] || []),
              event,
            ],
          };
        }, {});
        Object.keys(scheduleByDay).forEach((date) => {
          scheduleByDay[date].sort((a, b) => {
            const timeA = moment(a.eventData.startTime);
            const timeB = moment(b.eventData.startTime);
            return timeA.diff(timeB);
          });
        });
        resolve(scheduleByDay);
      }, {showProgress: false});
    });
  }, [scheduleSource, progressWrap, showInactive]);

  const getUserRn = React.useCallback((user) => user.clientRn, []);

  const SourceUserRow = React.useCallback(
      (rowProps) => <UserRow {...rowProps} userPath={scheduleSource.clientPath} />,
      [scheduleSource.clientPath],
  );

  const SourceEventTile = React.useCallback(
      (tileProps) => <EventTile {...tileProps} detailPath={scheduleSource.detailPath} />,
      [scheduleSource.detailPath],
  );

  return (
    <Box
      style={{
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Box
        style={{
          minWidth: '1184px',
          maxWidth: '2200px',
          width: '100%',
        }}
      >
        <Stack direction='row' justifyContent='flex-end' sx={{pb: 1}}>
          <SwitchCheckbox
            value={showInactive}
            onChange={setShowInactive}
            label={translate('weeklyScheduleCalendar.ShowInactiveShifts')}
          />
        </Stack>
        <WeeklyCalendarUsers
          headerContent={<Translate expression='client.ClientCalendar.Calendar.Details.Title' />}
          users={clients}
          getUserRn={getUserRn}
          loadUserSchedules={loadUserSchedules}
          UserRow={SourceUserRow}
          EventTile={SourceEventTile}
          loadingUsers={loadingClients && clients.length === 0}
          userFilter={userFilter}
          eventFilter={eventFilter}
        />
      </Box>
    </Box>
  );
};

ClientScheduleDashboard.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
  scheduleSource: PropTypes.object,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientScheduleDashboard));
